import * as React from "react";
import {Avatar, Box, Card, CardContent, Divider, Skeleton, Stack, Typography} from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import {useTranslation} from "react-i18next";
import {Contract} from "../../../../services/payrollService";
import {formatEUR} from "../../../../hooks/useDashboard";
import {periodBounds} from "../periodFormat";
import {getAvatarColors, getInitials} from "../../../../utils/avatarUtils";

interface PayrollPeriodEmployeesMobileProps {
    contracts: Contract[];
    contractsLoading: boolean;
    periodKey: string | null;
}

function isFullMonth(contract: Contract, periodKey: string | null): boolean {
    const bounds = periodBounds(periodKey);
    if (!bounds) return true;
    const start = contract.startDate ? new Date(contract.startDate) : null;
    const end = contract.endDate ? new Date(contract.endDate) : null;
    const startsBeforePeriod = !start || Number.isNaN(start.getTime()) || start <= bounds.start;
    const endsAfterPeriod = !end || Number.isNaN(end.getTime()) || end >= bounds.end;
    return startsBeforePeriod && endsAfterPeriod;
}

export default function PayrollPeriodEmployeesMobile({
    contracts,
    contractsLoading,
    periodKey,
}: Readonly<PayrollPeriodEmployeesMobileProps>): React.ReactElement {
    const {t} = useTranslation();

    if (contractsLoading) {
        return (
            <Stack spacing={1.5}>
                {Array.from({length: 3}).map((_, i) => (
                    <Card key={`employee-skeleton-mobile-${i}`} variant="outlined" sx={{borderRadius: 1}}>
                        <CardContent>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 1}}>
                                <Skeleton variant="circular" width={36} height={36}/>
                                <Skeleton variant="text" width={140}/>
                            </Box>
                            <Skeleton variant="text" width={100}/>
                            <Skeleton variant="text" width={160}/>
                        </CardContent>
                    </Card>
                ))}
            </Stack>
        );
    }

    if (contracts.length === 0) {
        return (
            <Card variant="outlined" sx={{borderRadius: 1}}>
                <CardContent sx={{textAlign: "center", py: 4}}>
                    <PeopleAltOutlinedIcon sx={{fontSize: 40, color: "text.disabled", mb: 1}}/>
                    <Typography variant="body2" color="text.secondary">
                        {t('payroll.period.employees.empty')}
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Stack spacing={1.5}>
            {contracts.map((contract) => {
                const avatarColors = getAvatarColors(`${contract.firstName}${contract.lastName}${contract.contractId}`);
                return (
                <Card key={contract.contractId} variant="outlined" sx={{borderRadius: 1}}>
                    <CardContent sx={{textAlign: "left"}}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar sx={{
                                width: 36,
                                height: 36,
                                bgcolor: avatarColors.bg,
                                color: avatarColors.color,
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                flexShrink: 0,
                            }}>
                                {getInitials(contract.firstName, contract.lastName)}
                            </Avatar>
                            <Box sx={{minWidth: 0}}>
                                <Typography variant="subtitle1" fontWeight={600} noWrap>
                                    {contract.firstName} {contract.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {contract.contractNumber}
                                </Typography>
                            </Box>
                        </Stack>
                        <Divider sx={{my: 1.5}}/>
                        <Box sx={{display: "flex", justifyContent: "space-between", mb: 0.5}}>
                            <Typography variant="caption" color="text.secondary">
                                {t('payroll.period.employees.header.basePay')}
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                                {formatEUR(Number(contract.baseAmount))} {contract.salaryBasis}
                            </Typography>
                        </Box>
                        <Box sx={{display: "flex", justifyContent: "space-between", mb: 0.5}}>
                            <Typography variant="caption" color="text.secondary">
                                {t('payroll.period.employees.header.workload')}
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                                {contract.workload}%
                            </Typography>
                        </Box>
                        <Box sx={{display: "flex", justifyContent: "space-between"}}>
                            <Typography variant="caption" color="text.secondary">
                                {t('payroll.period.employees.header.validity')}
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                                {isFullMonth(contract, periodKey)
                                    ? t('payroll.period.employees.validity.fullMonth')
                                    : t('payroll.period.employees.validity.partial')}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
                );
            })}
        </Stack>
    );
}
